import React from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Search, Filter } from 'lucide-react'

interface ServicesDirectoryCardProps {
  searchTerm: string
  onSearchChange: (value: string) => void
  statusFilter: 'all' | 'published' | 'draft' | 'archived'
  onStatusFilterChange: (value: 'all' | 'published' | 'draft' | 'archived') => void
  advancedFilterCount: number
  featuredFilter: 'all' | 'featured' | 'standard'
  footerFilter: 'all' | 'footer' | 'non-footer'
  serviceAreasOnly: boolean
  seoAuditMode: boolean
  onOpenAdvancedFilters: () => void
  onFeaturedFilterChange: (value: 'all' | 'featured' | 'standard') => void
  onFooterFilterChange: (value: 'all' | 'footer' | 'non-footer') => void
  onServiceAreasToggle: (value: boolean) => void
  onSeoAuditToggle: (value: boolean) => void
  onResetAdvancedFilters: () => void
}

export function ServicesDirectoryCard({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  advancedFilterCount,
  featuredFilter,
  footerFilter,
  serviceAreasOnly,
  seoAuditMode,
  onOpenAdvancedFilters,
  onFeaturedFilterChange,
  onFooterFilterChange,
  onServiceAreasToggle,
  onSeoAuditToggle,
  onResetAdvancedFilters,
}: ServicesDirectoryCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Service Directory</CardTitle>
        <CardDescription>Manage all your masonry services, content, and SEO settings.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-1 gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search services..."
                value={searchTerm}
                onChange={(event) => onSearchChange(event.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(event) => onStatusFilterChange(event.target.value as typeof statusFilter)}
              className="px-3 py-2 border border-input rounded-md bg-background text-sm"
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div className="flex gap-2">
            {advancedFilterCount > 0 && (
              <Badge variant="secondary" className="items-center hidden sm:inline-flex">
                {advancedFilterCount} active filter{advancedFilterCount > 1 ? 's' : ''}
              </Badge>
            )}
            <Button
              variant={advancedFilterCount > 0 ? 'default' : 'outline'}
              size="sm"
              onClick={onOpenAdvancedFilters}
            >
              <Filter className="mr-2 h-4 w-4" />
              {advancedFilterCount > 0 ? 'Adjust Filters' : 'More Filters'}
            </Button>
            {advancedFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={onResetAdvancedFilters}>
                Clear
              </Button>
            )}
          </div>
        </div>

        {advancedFilterCount > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {featuredFilter !== 'all' && (
              <Badge variant="outline" className="cursor-pointer" onClick={() => onFeaturedFilterChange('all')}>
                {featuredFilter === 'featured' ? 'Featured only' : 'Exclude featured'}
              </Badge>
            )}
            {footerFilter !== 'all' && (
              <Badge variant="outline" className="cursor-pointer" onClick={() => onFooterFilterChange('all')}>
                {footerFilter === 'footer' ? 'Footer pages' : 'Hide footer services'}
              </Badge>
            )}
            {serviceAreasOnly && (
              <Badge variant="outline" className="cursor-pointer" onClick={() => onServiceAreasToggle(false)}>
                Requires service areas
              </Badge>
            )}
            {seoAuditMode && (
              <Badge variant="outline" className="cursor-pointer" onClick={() => onSeoAuditToggle(false)}>
                Needs SEO attention
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
